"use client";

import { useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { usersApi } from "@/lib/api/users";
import { encryptPassword } from "@/lib/crypto";
import { useAuthStore } from "@/stores/auth-store";
import { useTranslations } from "@/i18n/useTranslations";
import { Loader2, KeyRound, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps) {
  const t = useTranslations("users");
  const tCommon = useTranslations("common");
  const router = useNavigate();
  const user = useAuthStore((state) => state.user);

  const changePasswordSchema = useMemo(
    () =>
      z
        .object({
          oldPassword: z.string().min(1, t("validation.currentPasswordRequired")),
          newPassword: z.string().min(8, t("validation.passwordMinLength")),
          confirmPassword: z.string().min(1, t("validation.confirmPasswordRequired")),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t("validation.passwordMismatch"),
          path: ["confirmPassword"],
        }),
    [t]
  );

  type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

  const form = useForm<ChangePasswordFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(changePasswordSchema) as any,
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordFormData) => {
      if (!user) throw new Error("No user logged in");
      const { password: encryptedOldPassword, encrypted: e1 } = await encryptPassword(data.oldPassword);
      const { password: encryptedNewPassword, encrypted: e2 } = await encryptPassword(data.newPassword);
      return usersApi.changePassword(user.id, {
        oldPassword: encryptedOldPassword,
        newPassword: encryptedNewPassword,
        encrypted: e1 || e2,
      });
    },
    onSuccess: () => {
      toast.success(t("changePasswordSuccess"), {
        description: t("changePasswordSuccessDesc"),
      });
      onOpenChange(false);
      // Redirect to login page since refresh token is revoked
      router("/login");
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : t("changePasswordErrorDesc");
      // Check if it's an old password error
      if (message.includes("old password") || message.includes("当前密码")) {
        toast.error(t("wrongOldPassword"), {
          description: message,
        });
      } else {
        toast.error(t("changePasswordError"), {
          description: message,
        });
      }
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate(data);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            {t("changePassword")}
          </DialogTitle>
          <DialogDescription>
            {t("changePasswordDesc")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            id="change-password-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            {/* Old Password */}
            <FormField
              control={form.control}
              name="oldPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-warning-text" />
                    {t("oldPassword")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("oldPasswordPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* New Password */}
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-success-text" />
                    {t("newPassword")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("newPasswordPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Confirm Password */}
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    {t("confirmPassword")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("confirmPasswordPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            form="change-password-form"
            disabled={changePasswordMutation.isPending}
            className="bg-gradient-to-r from-primary to-accent hover:from-primary hover:to-accent text-primary-foreground"
          >
            {changePasswordMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {tCommon("loading")}
              </>
            ) : (
              t("changePassword")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
